import { useLazyQuery } from 'apollo-client-ppc';
import defaultTo from 'lodash/defaultTo';
import capitalize from 'lodash/capitalize';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import React from 'react';
import Button from '@atlaskit/button';
import { ErrorMessage, Field } from '@atlaskit/form';
import Select, { OptionType, ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { EntitlementIcon, withApollo, useFeatureToggle } from '@css-xp/base';
import { ChargeElementData } from '@atlassian/cssxp-ccp-types';
import { ProxyGraphClients } from '@atlassian/temp-client-utils';
import { Offering, PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { ProductOffering } from '@css-xp/graphtypes/src/generated-types-sxp';
import {
  getChargeElement,
  isLoomProduct,
  isOfferingIncluded,
  loomSortedOfferingList,
  ROVO_PAID_OFFERING_ID,
} from '@css-xp/ppc-utils';
import { LinkAppToParentProduct } from './link-app-to-parent-product';
import { ManageAppsPromocode } from './manage-apps-promocode';
import { usePartnerCatalogueContext } from './context/partner-catalogue-context';
import { CheckoutOperation, usePartnerCheckoutNotification } from './hooks/use-place-order-notification';
import { fetchOfferingsAndAvailablePricingPlans } from './queries/sxp-queries/fetch-offerings-and-available-pricing-plans';
import { isFreeEdition, validateUserCount } from './utils';
import { useGetMaxUsersForOrderEstimate } from './hooks/use-get-max-users-for-order-estimate';
import { JIRA_CART_ITEM_ID } from './utils/constants';
import './partner-quote-catalog.scss';

export type PartnerQuoteCatalogueItemProps = {
  product: PartnerProductCatalogueItem;
  index: number;
  setEstimates: (estimates: { [key: string]: number }) => void;
  estimates: { [key: string]: number };
  setUserCountInMasterMap: (id: string, numberOfUsers: number) => void;
  parentFormRef: React.MutableRefObject<any>;
  isJiraNode: boolean;
};

type CatalogueOffering = Offering & {
  pricingType: string;
  chargeElements: Array<{ type: string; data: ChargeElementData }>;
};

const PartnerQuoteCatalogueItemImpl = ({
  product,
  index,
  setEstimates,
  estimates,
  setUserCountInMasterMap,
  parentFormRef,
  isJiraNode,
}: PartnerQuoteCatalogueItemProps) => {
  const [isEnabled] = useFeatureToggle();
  const [currentOffering, setCurrentOffering] = React.useState<OptionType | null>(null);
  const [userCount, setUserCount] = React.useState<number>(1);
  const isJSMStandardOfferingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE);
  const isCompassPremiumEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM);
  const isLoomEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STANDALONE_LOOM_FOR_UOF);
  const isJPDPremiumOfferingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING);
  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);

  const availableOfferingForProduct = product;
  const [areOfferingsForProductLoading, setAreOfferingsForProductLoading] = React.useState<boolean>(false);
  const {
    setIfCartItemValid,
    removeCartItemValidity,
    removeAppsParentEntitlement,
    removeAppsPromotionId,
    removeAppsPromotionIdsDetails,
    sandboxCloudSiteParentProductIds,
  } = usePartnerCatalogueContext();

  React.useEffect(() => {
    setIfCartItemValid(isJiraNode ? JIRA_CART_ITEM_ID : product.id, true);
    return () => {
      removeCartItemValidity(isJiraNode ? JIRA_CART_ITEM_ID : product.id);
    };
  }, []);

  // This represents the max number of users OMS can handle to return correct order estimate
  // with correct number of tiers in the response
  const [maxUserLimitForCurrentPricingPlan, setMaxUserLimitForCurrentPricingPlan] = React.useState<
    number | undefined | null
  >();

  const {
    setCatalogueItem,
    pricingPlanCycle,
    setQuoteFormData,
    setIsPartnerCatalogueInformationLoading,
    setPricingPlanData,
    setProductTrial,
    removeProductTrial,
    setProductMaxUsers,
    removeProductMaxUsers,
    setOtherPricingPlansForCurrentOffering,
  } = usePartnerCatalogueContext();
  const [fetchOfferingsWithPricingPlans] = useLazyQuery(fetchOfferingsAndAvailablePricingPlans);

  const { getMaxUsersForOrderEstimate } = useGetMaxUsersForOrderEstimate();

  let formData = {};
  if (parentFormRef.current) {
    formData = parentFormRef.current()?.values;
  }

  React.useEffect(() => {
    if (formData) {
      setQuoteFormData(formData);
    }
  }, [JSON.stringify(formData)]);

  const { notifyFailure } = usePartnerCheckoutNotification();

  React.useEffect(() => {
    (async () => {
      if (currentOffering && product && product.id) {
        try {
          setIsPartnerCatalogueInformationLoading(true);

          const availablePricingPlans = (availableOfferings.find(
            (offering) => offering.id === currentOffering.value
          ) as any)?.availablePricingPlans;

          const defaultCommercialPricingPlan = availablePricingPlans?.find(
            (plan: { pricingPlanType: string; type: string; status: string }) =>
              plan.pricingPlanType === 'DEFAULT_PRICING' && plan.type === 'COMMERCIAL' && plan.status === 'ACTIVE'
          );
          const pricingPlanWithDefaultPricingId = defaultCommercialPricingPlan?.id;

          const activeDefaultPricingPlans = availablePricingPlans?.filter(
            (plan: { status: string; pricingPlanType: string }) =>
              plan.status === 'ACTIVE' && plan.pricingPlanType === 'DEFAULT_PRICING'
          );

          const pricingEligibility = activeDefaultPricingPlans?.reduce(
            (acc: Record<string, string>, plan: { type: string; id: string }) => {
              return {
                ...acc,
                [plan.type]: plan.id,
              };
            },
            {}
          );

          const maxNumberOfUsersForOrderEstimate = getMaxUsersForOrderEstimate(
            defaultCommercialPricingPlan,
            currentOffering.chargeElements?.[0]?.data?.ceiling
          );

          setProductTrial(product.id, currentOffering.trialLengthInDays);
          setProductMaxUsers(product.id, maxNumberOfUsersForOrderEstimate);
          setPricingPlanData(product.id, pricingPlanWithDefaultPricingId);
          setOtherPricingPlansForCurrentOffering(product.id, pricingEligibility);
          setMaxUserLimitForCurrentPricingPlan(maxNumberOfUsersForOrderEstimate);
        } catch {
          notifyFailure('Please contact', CheckoutOperation.Others);
        } finally {
          setIsPartnerCatalogueInformationLoading(false);
        }
      }
    })();
  }, [currentOffering]);

  //@ts-ignore
  const availableOfferings: CatalogueOffering[] = React.useMemo(() => {
    let offerings = defaultTo(get(availableOfferingForProduct, 'offering'), []);

    // Changing the offering order for Loom products - https://hello.jira.atlassian.cloud/browse/TTC-32245
    if (isLoomEnabled && isLoomProduct(product.id)) {
      /* This will place the offerings in the order mentioned in the loomSortedOfferingList array at top
      without changing the order for rest of the offerings */
      offerings = sortBy(offerings, (offering) => {
        const index = loomSortedOfferingList.indexOf(offering.id);
        return index === -1 ? Infinity : index;
      });
    }

    return offerings;
  }, [availableOfferingForProduct, isLoomEnabled, product.id]);
  // Selecting the default offering for a product
  React.useEffect(() => {
    if (availableOfferings && availableOfferings?.length > 0) {
      const standardOffering: CatalogueOffering | undefined = (availableOfferings as CatalogueOffering[]).find(
        // there can be multiple offerings with name Standard, selecting the one which is in included list
        (offering: Offering) =>
          sandboxCloudSiteParentProductIds.includes(product.id)
            ? offering.offeringName.toUpperCase() === 'STANDARD SANDBOX'
            : offering.offeringName.toUpperCase() === 'STANDARD' &&
              isOfferingIncluded(offering, {
                isEnterpriseOfferingEnabled: false,
                isJSMRepackagingEnabled: isJSMStandardOfferingEnabled,
                isCompassPremiumEnabled,
                isJPDPremiumOfferingEnabled,
                isJPDAsJiraFamilyEnabled,
              })
      );
      const rovoPaidOffering: CatalogueOffering | undefined = (availableOfferings as CatalogueOffering[]).find(
        (offering: Offering) => offering.id === ROVO_PAID_OFFERING_ID
      );
      if (standardOffering) {
        setCurrentOffering({
          value: standardOffering.id,
          label: standardOffering.offeringName,
          pricingType: standardOffering.pricingType,
          trialLengthInDays: standardOffering.trialLengthInDays,
          chargeElements: standardOffering.chargeElements,
        });
      } else {
        const defaultNonStandardOffering: CatalogueOffering = rovoPaidOffering
          ? rovoPaidOffering
          : availableOfferings[0];
        setCurrentOffering({
          value: defaultNonStandardOffering?.id,
          label: defaultNonStandardOffering?.offeringName,
          pricingType: defaultNonStandardOffering.pricingType,
          trialLengthInDays: defaultNonStandardOffering.trialLengthInDays,
          chargeElements: defaultNonStandardOffering.chargeElements,
        });
      }
    }
  }, [availableOfferings]);

  // TODO: find all the sandbox offerings and add to config and remove this.
  const excludedOfferings = ['sandbox'];
  /* This will remove all the offerings having "substring" mentioned in excludedOfferings list */
  const filterExcludedOffering = (plan: Offering) => {
    return !excludedOfferings.some((excluded) => plan.offeringName.toLowerCase().includes(excluded));
  };

  const offeringOptions = React.useMemo(() => {
    const filteredOfferings = defaultTo(availableOfferings, []).filter(
      (plan) =>
        filterExcludedOffering(plan) &&
        isOfferingIncluded(plan, {
          isEnterpriseOfferingEnabled: false,
          isJSMRepackagingEnabled: isJSMStandardOfferingEnabled,
          isCompassPremiumEnabled,
          isJPDPremiumOfferingEnabled,
          isJPDAsJiraFamilyEnabled,
        })
    );

    return filteredOfferings.map((plan) => ({
      label: plan.offeringName,
      value: plan.id,
      pricingType: plan.pricingType,
      trialLengthInDays: plan.trialLengthInDays,
      chargeElements: plan.chargeElements,
    }));
  }, [availableOfferings]);

  return (
    <div
      key={index}
      className="ppc-checkout-catalogue-item-container"
      data-testid="ppc-checkout-catalogue-item-container"
    >
      <div className="ppc-item-header">
        <Field name={'product.' + index} defaultValue={product}>
          {() => (
            <div className="item-name-container">
              <EntitlementIcon
                productId={get(product, 'id')}
                logoId={get(product, 'logoId')}
                className="avatar large"
                isCloud
              />{' '}
              <h1 className="item-name">{get(product, 'name')}</h1>
            </div>
          )}
        </Field>
        <Button
          appearance="link"
          testId="remove-line-item"
          onClick={() => {
            removeProductTrial(product.id);
            removeProductMaxUsers(product.id);
            setCatalogueItem({ ...product });
            setEstimates({ ...estimates, [get(product, 'id')]: 0 });
            removeCartItemValidity(product.id);
            removeAppsParentEntitlement(product.id);
            removeAppsPromotionId(product.id);
            removeAppsPromotionIdsDetails(product.id); // NOTE: This is required only for unified order flow
          }}
        >
          Remove
        </Button>
      </div>
      {!product?.isApp && (
        <>
          <div className="ppc-catalogue-item-options">
            <Field<ValueType<OptionType>, HTMLSelectElement>
              label="Plan"
              name={'plan.' + `${get(product, 'id')}`}
              isRequired
              defaultValue={currentOffering}
            >
              {({ fieldProps }) => (
                <div className="actions" data-testid="plan-btn">
                  <Select<OptionType>
                    {...fieldProps}
                    isSearchable={false}
                    options={offeringOptions}
                    isLoading={areOfferingsForProductLoading}
                    onChange={(item: ValueType<OptionType>) => {
                      fieldProps.onChange(item);
                      setCurrentOffering(defaultTo(item, null));
                    }}
                  />
                </div>
              )}
            </Field>
            {currentOffering && !isFreeEdition(currentOffering.pricingType) && (
              <Field
                label={capitalize(getChargeElement({ productId: product.id, labelPlural: true }))}
                name={'numberOfUsers.' + `${get(product, 'id')}`}
                defaultValue={userCount}
                validate={validateUserCount({
                  ceiling: currentOffering.chargeElements?.[0]?.data?.ceiling,
                  plan: currentOffering.label,
                  maxNumberOfUsersForOrderEstimate: maxUserLimitForCurrentPricingPlan,
                })}
                isRequired
              >
                {({ fieldProps, error }) => (
                  <>
                    <div className="action number-of-users-txt">
                      <Textfield
                        defaultValue={userCount}
                        type="number"
                        min={1}
                        max={currentOffering.chargeElements?.[0]?.data?.ceiling}
                        {...fieldProps}
                        value={userCount}
                        onChange={(e: any) => {
                          fieldProps.onChange(e);
                          const enteredNumberOfUsers = e?.target?.value;

                          if (/[a-z]/i.test(enteredNumberOfUsers)) {
                            return;
                          }
                          setUserCount(enteredNumberOfUsers);
                          setUserCountInMasterMap(product.id, enteredNumberOfUsers);
                        }}
                        testId="number-of-users-txt"
                      />
                    </div>
                    {error && (
                      <div>
                        <ErrorMessage>{error}</ErrorMessage>
                      </div>
                    )}
                  </>
                )}
              </Field>
            )}
          </div>
        </>
      )}

      {product?.isApp && (
        <>
          <div className="ppc-catalogue-app-options" data-testId="linked-entitlement-table">
            <Field name={'plan.' + `${get(product, 'id')}`} defaultValue={currentOffering}>
              {() => <></>}
            </Field>
            <LinkAppToParentProduct product={product} offeringLabel={defaultTo(get(currentOffering, 'label'), '')} />
          </div>
          <div className="ppc-catalogue-item-options" data-testId="manage-promotion-section">
            <ManageAppsPromocode productId={get(product, 'id')} isPromotionDetailHidden={false} />
          </div>
        </>
      )}
    </div>
  );
};

export const PartnerQuoteCatalogueItem = withApollo(PartnerQuoteCatalogueItemImpl, ProxyGraphClients.SXP);
